import { createTheme, ThemeOptions } from '@mui/material/styles';
import { createContext, useEffect, useMemo, useState } from 'react';

// Create language & direction context
export const DirectionContext = createContext<{
  direction: 'ltr' | 'rtl';
  toggleDirection: () => void;
}>({
  direction: 'ltr',
  toggleDirection: () => {},
});

export const useDirection = () => {
  const [direction, setDirection] = useState<'ltr' | 'rtl'>(() => {
    // Get initial direction from localStorage or default to ltr
    const savedLang = localStorage.getItem('i18nextLng');
    return savedLang === 'ar' ? 'rtl' : 'ltr';
  });

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      const currentLang = localStorage.getItem('i18nextLng');
      setDirection(currentLang === 'ar' ? 'rtl' : 'ltr');
    };

    // Set up event listener for storage changes
    window.addEventListener('storage', handleLanguageChange);
    
    // Clean up
    return () => {
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const toggleDirection = () => {
    setDirection((prev) => (prev === 'ltr' ? 'rtl' : 'ltr'));
  };

  return { direction, toggleDirection };
};

export const getTheme = (direction: 'ltr' | 'rtl') => {
  const themeOptions: ThemeOptions = {
    direction,
    palette: {
      mode: 'light',
      primary: {
        main: '#101D68', // Brand Blue
        light: '#2A3A8A', // Lighter blue for hover states
        dark: '#0A1445', // Darker blue for active states
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#b22222', // Brand Red
        light: '#d32f2f', // Lighter red
        dark: '#8b1a1a', // Darker red
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#b22222',
        light: '#d32f2f',
        dark: '#8b1a1a',
      },
      background: {
        default: '#F5F7FA', // Soft gray background
        paper: '#FFFFFF',
      },
      text: {
        primary: '#1A1A1A',
        secondary: '#6B7280',
      },
      grey: {
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#6B7280',
        600: '#4B5563',
        700: '#374151',
        800: '#1F2937',
        900: '#111827',
      },
    },
    typography: {
      fontFamily: [
        ...(direction === 'rtl' 
          ? ['"Noto Sans Arabic"', 'Tahoma', 'Arial'] 
          : ['"Inter"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif']),
      ].join(','),
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 1px 2px rgba(16, 29, 104, 0.05)',
      '0px 2px 4px rgba(16, 29, 104, 0.05)',
      '0px 4px 8px rgba(16, 29, 104, 0.08)',
      '0px 8px 16px rgba(16, 29, 104, 0.1)',
      '0px 12px 24px rgba(16, 29, 104, 0.12)',
      '0px 16px 32px rgba(16, 29, 104, 0.15)',
      '0px 20px 40px rgba(16, 29, 104, 0.18)',
      '0px 24px 48px rgba(16, 29, 104, 0.2)',
      '0px 28px 56px rgba(16, 29, 104, 0.22)',
      '0px 32px 64px rgba(16, 29, 104, 0.24)',
      '0px 36px 72px rgba(16, 29, 104, 0.26)',
      '0px 40px 80px rgba(16, 29, 104, 0.28)',
      '0px 44px 88px rgba(16, 29, 104, 0.3)',
      '0px 48px 96px rgba(16, 29, 104, 0.32)',
      '0px 52px 104px rgba(16, 29, 104, 0.34)',
      '0px 56px 112px rgba(16, 29, 104, 0.36)',
      '0px 60px 120px rgba(16, 29, 104, 0.38)',
      '0px 64px 128px rgba(16, 29, 104, 0.4)',
      '0px 68px 136px rgba(16, 29, 104, 0.42)',
      '0px 72px 144px rgba(16, 29, 104, 0.44)',
      '0px 76px 152px rgba(16, 29, 104, 0.46)',
      '0px 80px 160px rgba(16, 29, 104, 0.48)',
      '0px 84px 168px rgba(16, 29, 104, 0.5)',
      '0px 88px 176px rgba(16, 29, 104, 0.52)',
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(16, 29, 104, 0.15)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0px 6px 12px rgba(16, 29, 104, 0.2)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
          },
          elevation1: {
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
          },
          elevation2: {
            boxShadow: '0px 4px 12px rgba(16, 29, 104, 0.1)',
          },
          elevation3: {
            boxShadow: '0px 6px 16px rgba(16, 29, 104, 0.12)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0px 8px 24px rgba(16, 29, 104, 0.12)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#101D68',
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.1)',
            background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
            boxShadow: '2px 0px 8px rgba(16, 29, 104, 0.08)',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '4px 8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(16, 29, 104, 0.08)',
              transform: 'translateX(4px)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(16, 29, 104, 0.12)',
              '&:hover': {
                backgroundColor: 'rgba(16, 29, 104, 0.16)',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            boxShadow: '0px 12px 48px rgba(16, 29, 104, 0.2)',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Default theme (LTR)
const theme = createTheme(getTheme('ltr'));

export default theme; 